/**
 * Tâche 1149 (Eric 2026-06-16) — Refus du fournisseur de courriel.
 *
 * Quand Resend refuse un envoi, le **motif exact** qu'il renvoie (« domain not
 * verified », « invalid recipient », quota dépassé…) est la seule information
 * qui dit quoi corriger. Affiché à l'écran, jamais par courriel : un courriel
 * annonçant un refus de courriel serait refusé de la même façon.
 *
 * Tâche 1153 (2026-06-17) — Les refus temporaires sont rejoués tout seuls. La
 * file est affichée ici, avec « Réessayer maintenant » : celui qui vient de
 * corriger la cause n'a pas à attendre le prochain créneau.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowsClockwise, WarningOctagon } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export const ProviderRefusalPanel = () => {
  const [data, setData] = useState(null);
  const [retries, setRetries] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/system-notifications/provider-refusals?days=7`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch {
      setData(null);
    }
    try {
      const res = await axios.get(
        `${API}/api/system-notifications/email-retries`,
        { withCredentials: true }
      );
      setRetries(res.data);
    } catch {
      setRetries(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const retryNow = async () => {
    setRetrying(true);
    try {
      const res = await axios.post(
        `${API}/api/system-notifications/email-retries/retry-now`,
        {},
        { withCredentials: true }
      );
      if (res.data?.summary) setRetries(res.data.summary);
      const { sent = 0, rescheduled = 0, abandoned = 0 } = res.data || {};
      if (sent > 0) toast.success(`${sent} courriel(s) enfin parti(s)`);
      else if (rescheduled > 0)
        toast.info(
          `Toujours refusé : ${rescheduled} envoi(s) reporté(s) à plus tard`
        );
      else if (abandoned > 0)
        toast.error(`${abandoned} envoi(s) abandonné(s) — motif définitif`);
      else toast.info("Aucun envoi en attente de réessai");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réessai impossible");
    } finally {
      setRetrying(false);
    }
  };

  const pending = Number(retries?.pending) || 0;
  if (!data?.total && !pending) return null;

  return (
    <section
      className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3"
      data-testid="provider-refusals"
    >
      <div className="flex items-start gap-3">
        <WarningOctagon size={20} className="mt-0.5 shrink-0 text-red-600" />
        <div className="text-sm text-red-900 w-full">
          <p className="font-semibold" data-testid="provider-refusals-count">
            {data?.total || 0} courriel(s) refusé(s) par le fournisseur sur les{" "}
            {data?.days || 7} derniers jours
          </p>
          <ul className="mt-2 space-y-2">
            {(data?.reasons || []).map((r) => (
              <li
                key={r.reason}
                className="rounded-lg bg-white/70 px-3 py-2"
                data-testid="provider-refusal-reason"
              >
                <div className="font-mono text-xs text-red-800 break-words">
                  {r.reason}
                </div>
                <div className="mt-1 text-xs text-red-700">
                  {r.count} envoi(s) · {r.recipients.join(", ")}
                  {r.last_at
                    ? ` · dernier : ${r.last_at.slice(0, 16).replace("T", " ")}`
                    : ""}
                </div>
              </li>
            ))}
          </ul>

          <div
            className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/70 px-3 py-2"
            data-testid="email-retry-queue"
          >
            <div className="text-xs text-red-800">
              {pending > 0 ? (
                <span data-testid="email-retry-pending">
                  {pending} envoi(s) en attente de réessai automatique
                  {retries?.next_attempt_at
                    ? ` · prochaine tentative : ${retries.next_attempt_at
                        .slice(0, 16)
                        .replace("T", " ")}`
                    : ""}
                </span>
              ) : (
                <span data-testid="email-retry-empty">
                  Aucun envoi en attente de réessai
                </span>
              )}
              <span className="block opacity-80 mt-0.5">
                {retries?.sent || 0} rejoué(s) avec succès ·{" "}
                {retries?.abandoned || 0} abandonné(s) sur 24 h. Les motifs
                définitifs (domaine non vérifié, adresse invalide) ne sont
                jamais rejoués.
              </span>
            </div>
            <button
              type="button"
              onClick={retryNow}
              disabled={retrying}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-300 bg-white text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
              data-testid="email-retry-now"
            >
              <ArrowsClockwise size={16} />
              {retrying ? "Réessai…" : "Réessayer maintenant"}
            </button>
          </div>

          <p className="mt-2 text-xs text-red-700">
            Motif renvoyé tel quel par Resend. Corrigez la cause (domaine
            vérifié, adresse valide, quota) puis relancez un test.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProviderRefusalPanel;
